import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X } from '@phosphor-icons/react';
import api from '../lib/api';
import { extractErrorMessage } from '../lib/errors';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { useT } from '../i18n/I18nContext';

const blankRepo = () => ({ name: '', gitlab_url: '', gitlab_token: '', default_branch: 'main' });

const initialForm = () => ({
  name: '',
  description: '',
  repos: [],
});

export default function NewProjectDialog({ open, onOpenChange, onProjectsChange }) {
  const nav = useNavigate();
  const t = useT();
  const [form, setForm] = useState(initialForm);
  const [creating, setCreating] = useState(false);

  const updateRepo = (idx, patch) => {
    setForm((f) => ({
      ...f,
      repos: f.repos.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  };

  const addRepo = () => {
    setForm((f) => ({
      ...f,
      repos: [...f.repos, { ...blankRepo(), name: f.repos.length === 0 ? 'main' : '' }],
    }));
  };

  const removeRepo = (idx) => {
    setForm((f) => ({ ...f, repos: f.repos.filter((_, i) => i !== idx) }));
  };


  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        repos: form.repos.map((r) => ({
          name: (r.name || '').trim() || 'main',
          gitlab_url: r.gitlab_url.trim(),
          gitlab_token: r.gitlab_token.trim(),
          default_branch: (r.default_branch || 'main').trim() || 'main',
        })),
      };
      const { data } = await api.post('/projects', payload);
      toast.success(t('newProject.createdToast', { name: data.name }));
      onOpenChange(false);
      setForm(initialForm());
      await onProjectsChange();
      nav(`/projects/${data.id}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, t('newProject.failedToast')));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setForm(initialForm());
      }}
    >
      <DialogContent className="rounded-sm sm:max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-3 border-b border-zinc-200">
          <DialogTitle className="font-display font-black tracking-tight">{t('newProject.title')}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">{t('newProject.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={create} className="flex flex-col min-h-0 flex-1">
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">{t('newProject.nameLabel')}</Label>
              <Input
                data-testid="project-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">{t('newProject.descriptionLabel')}</Label>
              <Textarea
                data-testid="project-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="rounded-sm mt-1"
                rows={2}
              />
            </div>

            <div className="space-y-2" data-testid="project-repos-form">
              <div className="flex items-baseline justify-between">
                <Label className="text-xs uppercase tracking-wider font-semibold">{t('newProject.reposTitle')}</Label>
                <span className="text-[10px] text-zinc-500 font-mono">{form.repos.length}</span>
              </div>
              <p className="text-[11px] text-zinc-500">{t('newProject.reposHint')}</p>
              {form.repos.length === 0 && (
                <div
                  className="rounded-sm border border-dashed border-zinc-300 bg-zinc-50 px-3 py-4 text-center"
                  data-testid="project-repos-empty"
                >
                  <p className="text-[11px] text-zinc-500">
                    {t('newProject.reposOptionalHint')}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                {form.repos.map((repo, idx) => (
                  <div
                    key={idx}
                    className="rounded-sm border border-zinc-200 bg-white p-3 space-y-2"
                    data-testid={`project-repo-row-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">
                        #{idx + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeRepo(idx)}
                        data-testid={`project-repo-remove-${idx}`}
                        className="w-6 h-6 rounded-sm hover:bg-red-50 text-zinc-500 hover:text-red-600 flex items-center justify-center"
                        title={t('newProject.removeRepoAria')}
                        aria-label={t('newProject.removeRepoAria')}
                      >
                        <X size={12} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('newProject.repoNameLabel')}</Label>
                        <Input
                          data-testid={`project-repo-name-input-${idx}`}
                          value={repo.name}
                          placeholder={t('newProject.repoNamePlaceholder')}
                          onChange={(e) => updateRepo(idx, { name: e.target.value })}
                          className="rounded-sm mt-1 font-mono text-xs"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('newProject.defaultBranchLabel')}</Label>
                        <Input
                          data-testid={`project-repo-branch-input-${idx}`}
                          value={repo.default_branch}
                          onChange={(e) => updateRepo(idx, { default_branch: e.target.value })}
                          className="rounded-sm mt-1 font-mono text-xs"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('newProject.gitlabUrlLabel')}</Label>
                        <Input
                          data-testid={`project-repo-url-input-${idx}`}
                          required
                          placeholder="https://gitlab.com/group/project"
                          value={repo.gitlab_url}
                          onChange={(e) => updateRepo(idx, { gitlab_url: e.target.value })}
                          className="rounded-sm mt-1 font-mono text-xs"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('newProject.gitlabTokenLabel')}</Label>
                        <Input
                          data-testid={`project-repo-token-input-${idx}`}
                          required
                          type="password"
                          placeholder="glpat-..."
                          value={repo.gitlab_token}
                          onChange={(e) => updateRepo(idx, { gitlab_token: e.target.value })}
                          className="rounded-sm mt-1 font-mono text-xs"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRepo}
                data-testid="project-add-repo-btn"
                className="rounded-sm"
              >
                <Plus size={12} className="mr-1.5" />
                {t('newProject.addRepoBtn')}
              </Button>
              <p className="text-[11px] text-zinc-500">
                {t('newProject.gitlabTokenHint', { api: 'api', writeRepo: 'write_repository' })}
              </p>
              <p className="text-[11px] text-zinc-500">
                {t('newProject.gitlabTokenInfo', { envVar: 'REPO_CACHE_DIR', defaultDir: 'backend/repos' })}
              </p>
            </div>
          </div>
          <DialogFooter className="px-6 py-3 border-t border-zinc-200 bg-white">
            <Button type="submit" data-testid="project-create-submit-btn" disabled={creating} className="rounded-sm bg-black text-white hover:bg-zinc-800">
              {creating ? t('newProject.submitting') : t('newProject.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
